import { a as addDays, f as isValidDateString, d as dateToDateString, i as isBefore, b as isAfter } from './index13.js';
import { n as normalizeDates } from './normalizeDates.js';
import { c as constructFrom } from './toDate.js';

function normalizeInterval(context, interval) {
  const [start, end] = normalizeDates(context, interval.start, interval.end);
  return { start, end };
}

/**
 * The {@link eachDayOfInterval} function options.
 */

/**
 * The {@link eachDayOfInterval} function result type. It resolves the proper data type.
 * It uses the first argument date object type, starting from the date argument,
 * then the start interval date, and finally the end interval date. If
 * a context function is passed, it uses the context function return type.
 */

/**
 * @name eachDayOfInterval
 * @category Interval Helpers
 * @summary Return the array of dates within the specified time interval.
 *
 * @description
 * Return the array of dates within the specified time interval.
 *
 * @typeParam IntervalType - Interval type.
 * @typeParam Options - Options type.
 *
 * @param interval - The interval.
 * @param options - An object with options.
 *
 * @returns The array with starts of days from the day of the interval start to the day of the interval end
 *
 * @example
 * // Each day between 6 October 2014 and 10 October 2014:
 * const result = eachDayOfInterval({
 *   start: new Date(2014, 9, 6),
 *   end: new Date(2014, 9, 10)
 * })
 * //=> [
 * //   Mon Oct 06 2014 00:00:00,
 * //   Tue Oct 07 2014 00:00:00,
 * //   Wed Oct 08 2014 00:00:00,
 * //   Thu Oct 09 2014 00:00:00,
 * //   Fri Oct 10 2014 00:00:00
 * // ]
 */
function eachDayOfInterval(interval, options) {
  const { start, end } = normalizeInterval(options?.in, interval);

  let reversed = +start > +end;
  const endTime = reversed ? +start : +end;
  const date = reversed ? end : start;
  date.setHours(0, 0, 0, 0);

  let step = options?.step ?? 1;
  if (!step) return [];
  if (step < 0) {
    step = -step;
    reversed = !reversed;
  }

  const dates = [];

  while (+date <= endTime) {
    dates.push(constructFrom(start, date));
    date.setDate(date.getDate() + step);
    date.setHours(0, 0, 0, 0);
  }

  return reversed ? dates.reverse() : dates;
}

/**
 * The {@link subDays} function options.
 */

/**
 * @name subDays
 * @category Day Helpers
 * @summary Subtract the specified number of days from the given date.
 *
 * @typeParam DateType - The `Date` type, the function operates on. Gets inferred from passed arguments. Allows to use extensions like [`UTCDate`](https://github.com/date-fns/utc).
 * @typeParam ResultDate - The result `Date` type, it is the type returned from the context function if it is passed, or inferred from the arguments.
 *
 * @param date - The date to be changed
 * @param amount - The amount of days to be subtracted.
 * @param options - An object with options
 *
 * @returns The new date with the days subtracted
 *
 * @example
 * // Subtract 10 days from 1 September 2014:
 * const result = subDays(new Date(2014, 8, 1), 10)
 * //=> Fri Aug 22 2014 00:00:00
 */
function subDays(date, amount, options) {
  return addDays(date, -amount, options);
}

const parseValue = (val) => {
    if (typeof val === 'undefined') {
        return;
    }
    const [start, end] = JSON.parse(val);
    const startValid = start === null || isValidDateString(start);
    const endValid = end === null || isValidDateString(end);
    if (!(startValid && endValid)) {
        throw new Error('Invalid date values');
    }
    return [start, end];
};
const stringifyValue = (range) => {
    const [start, end] = range.map((v) => (v instanceof Date ? dateToDateString(v) : v));
    return JSON.stringify([start, end]);
};
const getNewValue = (idx, newDate, currentValue) => {
    let newValue = [null, null];
    try {
        newValue = parseValue(currentValue) ?? [null, null];
    }
    catch (err) {
        // ignore bad values
    }
    // Update given idx with new Date object
    newValue[idx] = newDate || null;
    return newValue;
};
function checkDateBounds({ date, pos, minDate, maxDate, }) {
    // Nothing to check if date is undefined
    if (!date) {
        return;
    }
    // If we can compare to minDate
    if (typeof minDate !== 'undefined') {
        if (date !== minDate && isBefore(new Date(date), new Date(minDate))) {
            throw new Error(`The ${pos} date may not be before the min date.`);
        }
    }
    // If we can compare to maxDate
    if (typeof maxDate !== 'undefined') {
        if (date !== maxDate && isAfter(new Date(date), new Date(maxDate))) {
            throw new Error(`The ${pos} date may not be after the max date.`);
        }
    }
}
function checkDateOverlaps(startDate, endDate) {
    // Nothing to check if we don't have both dates
    if (!(startDate && endDate)) {
        return;
    }
    // Dates may be on the same day
    if (startDate === endDate) {
        return;
    }
    // If end date is before start date
    if (isBefore(new Date(endDate), new Date(startDate))) {
        throw new Error('The end date may not be before the start date.');
    }
}
function rangeWithinMaxDays({ start, end }, maxDays) {
    if (!maxDays) {
        return true;
    }
    if (!(start && end)) {
        return true;
    }
    const result = eachDayOfInterval({
        start,
        end,
    });
    return result.length <= maxDays;
}

export { checkDateBounds as a, subDays as b, checkDateOverlaps as c, getNewValue as g, parseValue as p, rangeWithinMaxDays as r, stringifyValue as s };

//# sourceMappingURL=util3.js.map